import { useQuery } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  DialogContent,
  Input,
  Select,
  SelectChangeEvent,
  SelectOption,
  Theme,
  palettes,
  useFlashMessage
} from '@indigo-ag/component-module';
import { Credit, ExploredAreas, FinancingMunicipality, FinancingState } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useEffect, useState } from 'react';

import {
  AutoComplete,
  EnhancedDialog,
  InputNumberMask,
  ModalActionButtons,
  createStylesHook,
  getMunicipalities,
  getStates,
  utils
} from 'shared';
import { ExploredAreasProps } from '../../ExploredAreas';

export interface ExploredAreasModalProps extends ExploredAreasProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

const useStyles = createStylesHook((theme: Theme) => ({
  flexDocument: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputStyle: {
    marginRight: '32px !important'
  },
  size14Black: {
    fontSize: theme.typography.pxToRem(14),
    color: palettes.neutral['700']
  }
}));

const SUGGESTIONS_LIMIT = 5;
const SHOW_SUGGESTIONS_AFTER = 0;

export const ExploredAreasModal: React.FunctionComponent<ExploredAreasModalProps> = ({
  closeModal,
  open,
  exploredAreasInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [areaName, setAreaName] = useState('');
  const [registration, setRegistration] = useState('');
  const [holder, setHolder] = useState('');
  const [registrationArea, setRegistrationArea] = useState(0);
  const [freeArea, setFreeArea] = useState(0);
  const [municipality, setMunicipality] = useState('');
  const [county, setCounty] = useState('');
  const [UF, setUF] = useState<FinancingState | null>(null);
  const [exploredAreas, setExploredAreas] = useState<ExploredAreas[]>(exploredAreasInfo?.exploredAreas || []);
  const [municipalityHint, setMunicipalityHint] = useState('');
  const [resetInput, setResetInput] = useState(false);
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * queries
   */
  const resultMunicipalities = useQuery(getMunicipalities, {
    variables: { where: { name: municipalityHint, limit: SUGGESTIONS_LIMIT } }
  });
  const resultStates = useQuery(getStates);

  const { data: municipalityData, loading: isFetchingMunicipalities } = resultMunicipalities;

  const { financingStates }: { financingStates: FinancingState[] } = resultStates.data || { financingStates: [] };
  const {
    financingMunicipalities: municipalities = []
  }: { financingMunicipalities: FinancingMunicipality[] } = municipalityData || { financingMunicipalities: [] };

  const getSuggestions = (hint: string) => setMunicipalityHint(hint);

  const handleAreaName = (event: React.ChangeEvent<HTMLInputElement>): void => setAreaName(event.target.value);

  const handleRegistration = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRegistration(event.target.value);
  };

  const handleHolder = (event: React.ChangeEvent<HTMLInputElement>): void => setHolder(event.target.value);

  const handleMunicipality = (option: SelectOption): void => {
    const selectedMunicipality = municipalities.find(({ id }) => id === option.value);
    if (selectedMunicipality) {
      const state = financingStates.find(({ id }) => id === selectedMunicipality.stateId.toString()) || null;
      setMunicipality(selectedMunicipality.name);
      setUF(state);
    }
  };

  const handleCounty = (event: React.ChangeEvent<HTMLInputElement>): void => setCounty(event.target.value);

  const handleUF = ({ target: { value: stateId } }: SelectChangeEvent<string>): void => {
    const state = financingStates.find(({ id }) => id === stateId);
    if (state) {
      setUF(state);
    }
  };

  const cleanExploredAreasSpace = (): void => {
    setAreaName('');
    setCounty('');
    setFreeArea(0);
    setHolder('');
    setMunicipality('');
    setRegistration('');
    setRegistrationArea(0);
    setUF(null);
  };

  useEffect(() => {
    cleanExploredAreasSpace();
    setExploredAreas(exploredAreasInfo?.exploredAreas || []);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNewExploredAreas = (): void => {
    setExploredAreas([
      ...exploredAreas,
      {
        name: areaName,
        registration: registration.toString(),
        holder,
        registrationArea,
        freeArea,
        municipality,
        county,
        UF: UF?.code
      }
    ]);
    cleanExploredAreasSpace();
    setResetInput(!resetInput);
  };

  const handleRemoveExploredArea = (area: number) => {
    return () => {
      const newExploredAreaList = exploredAreas.filter((_, index) => index !== area);
      setExploredAreas(newExploredAreaList);
    };
  };

  const isAddBtnDisabled = !areaName || !registrationArea;

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    const newExploredAreas = [...exploredAreas];
    if (!isAddBtnDisabled) {
      newExploredAreas.push({
        name: areaName,
        registration: registration.toString(),
        holder,
        registrationArea,
        freeArea,
        municipality,
        county,
        UF: UF?.code
      });
    }

    return updateCredit({
      exploredAreas: utils.graphql.removeTypename(newExploredAreas)
    }).then(() => {
      addFlashMessage({
        content: `${i18next.t('exploredAreas')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });
  };

  const updateFreeAreaValue = (value: number | null) => {
    setFreeArea(value || 0);
  };

  const updateRegistrationAreaValue = (value: number | null) => {
    setRegistrationArea(value || 0);
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('exploredAreas')}>
      <DialogContent>
        <Input label={i18next.t('name')} value={areaName} onChange={handleAreaName} />
        <div className={classes.flexDocument}>
          <Input
            label={i18next.t('registration')}
            variant="text"
            className={classes.inputStyle}
            value={registration || ''}
            onChange={handleRegistration}
          />
          <Input label={i18next.t('holder')} value={holder} onChange={handleHolder} />
        </div>
        <div className={classes.flexDocument}>
          <InputNumberMask
            label={i18next.t('registrationArea')}
            onNumberChange={updateRegistrationAreaValue}
            className={classes.inputStyle}
            reset={resetInput}
          />
          <InputNumberMask label={i18next.t('freeArea')} onNumberChange={updateFreeAreaValue} reset={resetInput} />
        </div>
        <AutoComplete
          label={i18next.t('municipality')}
          id="municipality"
          suggestions={municipalities.map(({ id, name }: FinancingMunicipality): SelectOption => ({ value: id, name }))}
          onChange={getSuggestions}
          onSelectOption={handleMunicipality}
          value={municipality}
          isLoading={isFetchingMunicipalities}
          suggestionsLimit={SUGGESTIONS_LIMIT}
          reset={resetInput}
          showSuggestionsAfter={SHOW_SUGGESTIONS_AFTER}
        />
        <div className={classes.flexDocument}>
          <Input label={i18next.t('county')} className={classes.inputStyle} value={county} onChange={handleCounty} />
          <Select
            placeholder={i18next.t('pleaseSelect')}
            label="UF"
            options={financingStates.map(({ id, name }: FinancingState): SelectOption => ({ name, value: id }))}
            name="options"
            value={UF?.id || null}
            onChange={handleUF}
          />
        </div>
        <Button size="small" variant="secondary" disabled={isAddBtnDisabled} onClick={addNewExploredAreas}>
          {i18next.t('add')}
        </Button>
        {exploredAreas.length > 0 && <p className={classes.size14Black}>{i18next.t('exploredAreas')}</p>}
        {exploredAreas &&
          exploredAreas.map((area, index: number) => (
            <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
              <span style={{ marginBottom: 10 }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  size="lg"
                  style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                />
                {` ${area.name}`}
              </span>
              <span
                onClick={handleRemoveExploredArea(index)}
                style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
              >
                {i18next.t('remove')}
              </span>
            </div>
          ))}
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={!(exploredAreas.length > 0) && isAddBtnDisabled}
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
